import type {
  ChronikEntry,
  ParagraphWithEntries,
  UserContentTabFoL,
} from "~/models/IBookshelf";
import type { EntryFilterFunction } from "~/composables/entries/schema/useSchemaFilters";

const filterParagraphs = (
  activeFilters: EntryFilterFunction<ChronikEntry>[],
  paragraphs: ParagraphWithEntries[]
) => {
  return paragraphs.reduce((allParagraphs, paragraph) => {
    const filteredEntries = paragraph.entries.filter((entry) =>
      activeFilters.some((filter) => filter(entry))
    );
    if (filteredEntries.length === 0) {
      return allParagraphs;
    }
    return [
      ...allParagraphs,
      {
        ...paragraph,
        entries: filteredEntries,
      },
    ];
  }, [] as ParagraphWithEntries[]);
};

const filterFieldOfLawRecursively = (
  activeFilters: EntryFilterFunction<ChronikEntry>[],
  fieldOfLaws: UserContentTabFoL[] = []
) =>
  fieldOfLaws.reduce((allFieldsOfLaw, fieldOfLaw) => {
    const filteredChildren = filterFieldOfLawRecursively(
      activeFilters,
      fieldOfLaw.children
    );

    const filteredParagraphs = filterParagraphs(
      activeFilters,
      fieldOfLaw.paragraphs
    );

    if (filteredParagraphs.length === 0 && filteredChildren.length === 0) {
      return allFieldsOfLaw;
    }
    return [
      ...allFieldsOfLaw,
      {
        ...fieldOfLaw,
        children: filteredChildren,
        paragraphs: filteredParagraphs,
      },
    ];
  }, [] as UserContentTabFoL[]);

export function useContentTabs(
  userContent: MaybeRefOrGetter<UserContentTabFoL[] | undefined>,
  filters: MaybeRefOrGetter<EntryFilterFunction<ChronikEntry>[]>
) {
  const filteredTabs = computed<UserContentTabFoL[]>(() => {
    const content = toValue(userContent) ?? [];

    return content.map((tab) => {
      const filteredFoLs = filterFieldOfLawRecursively(
        toValue(filters),
        tab.children
      );
      return {
        ...tab,
        children: filteredFoLs,
      };
    });
  });

  return { filteredTabs };
}
